import { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { addDepartment } from 'store/departmentmanagementSlice';
import { fetchCompanies } from 'store/companySlice';
import { createBulkDepartmentAllCompanies } from 'api/departmentAPI';
import { getEffectiveCompanyContext } from 'api/contextAPI';

interface Props {
  show: boolean;
  handleClose: () => void;
  companyId: number;
}

interface ApiError {
  message?: string;
  response?: {
    data?: {
      detail?: string;
    };
  };
}

const CreateDepartmentModal = ({ show, handleClose, companyId }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const userRole = useSelector((state: RootState) => state.auth.role);

  const { data: companies, loading: companiesLoading } = useSelector(
    (state: RootState) => state.companies
  );
  const { data: users } = useSelector((state: RootState) => state.users);

  const [name, setName] = useState('');
  const [headUserId, setHeadUserId] = useState<number | '' | null>(null);
  const [effectiveCompanyId, setEffectiveCompanyId] = useState<number | null>(
    null
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(companyId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createForAllCompanies, setCreateForAllCompanies] = useState(false);

  const isSuperAdmin = userRole === 'super_user';

  // Fetch effective company from context API
  useEffect(() => {
    const fetchEffectiveCompany = async () => {
      try {
        const context = await getEffectiveCompanyContext();
        const companyId = context.company_id || null;
        setEffectiveCompanyId(companyId);

        // Use effective company ID as default if not super admin
        if (!isSuperAdmin && companyId) {
          setSelectedCompanyId(companyId);
        }
      } catch (error) {
        console.error('Failed to fetch effective company:', error);
      }
    };

    if (show) {
      fetchEffectiveCompany();
      dispatch(fetchCompanies());
      if (!isSuperAdmin && effectiveCompanyId) {
        setSelectedCompanyId(effectiveCompanyId);
      } else if (companyId) {
        setSelectedCompanyId(companyId);
      }
    }
  }, [dispatch, show, companyId, isSuperAdmin, effectiveCompanyId]);

  const company = companies.find(c => c.id === selectedCompanyId);
  const companyExists = !!company;
  const companyName = company?.name || `Company #${selectedCompanyId}`;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Department name is required');
      return;
    }

    if (!createForAllCompanies && !companyExists) {
      setError(
        `Company #${selectedCompanyId} not found. Please select a valid company.`
      );
      return;
    }

    setLoading(true);
    try {
      if (createForAllCompanies) {
        // Create department for all companies
        await createBulkDepartmentAllCompanies({
          name: name.trim(),
          head_user_id:
            headUserId === '' || headUserId === null ? null : Number(headUserId)
        });
        // Refresh page to show newly created departments
        window.location.reload();
      } else {
        // Create department for specific company
        await dispatch(
          addDepartment({
            name: name.trim(),
            company_id: selectedCompanyId,
            head_user_id:
              headUserId === '' || headUserId === null
                ? null
                : Number(headUserId)
          })
        ).unwrap();
      }

      setName('');
      setHeadUserId(null);
      handleClose();
    } catch (err: unknown) {
      let errorMessage = 'Failed to create department';

      if (typeof err === 'object' && err !== null) {
        const apiError = err as ApiError;

        if (apiError.message) {
          errorMessage = apiError.message;
        } else if (apiError.response?.data?.detail) {
          errorMessage = apiError.response.data.detail;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      console.error('Failed to create department:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setName('');
    setHeadUserId(null);
    setError(null);
    setCreateForAllCompanies(false);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Department</Modal.Title>
      </Modal.Header>

      <Form onSubmit={onSubmit}>
        <Modal.Body>
          {/* Company Selection - Only for Super Admin */}
          {userRole === 'super_user' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Company *</Form.Label>
                <Form.Select
                  value={selectedCompanyId}
                  onChange={e => setSelectedCompanyId(Number(e.target.value))}
                  required={!createForAllCompanies}
                  disabled={loading || createForAllCompanies}
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="createForAllCompanies"
                  label="Create this department for all companies"
                  checked={createForAllCompanies}
                  onChange={e => setCreateForAllCompanies(e.target.checked)}
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  When checked, this department will be created for all existing
                  companies at once
                </Form.Text>
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Department Name *</Form.Label>
            <Form.Control
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter department name"
              required
              disabled={
                (createForAllCompanies ? false : !companyExists) || loading
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Department Head (Optional)</Form.Label>
            <Form.Select
              value={headUserId === null ? '' : headUserId}
              onChange={e =>
                setHeadUserId(
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              disabled={
                (createForAllCompanies ? false : !companyExists) || loading
              }
            >
              <option value="">No Department Head</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              {createForAllCompanies
                ? 'Note: Department head selection is not available for bulk creation'
                : 'Optional: Select a user to be the department head'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              (createForAllCompanies ? false : !companyExists) || loading
            }
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Creating...
              </>
            ) : createForAllCompanies ? (
              'Create Department for All Companies'
            ) : (
              'Create Department'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateDepartmentModal;
